import { getPublicReservationSettings } from '../../../core/services/publicReservationSettingsService';
import ReservationModificationNotice from './ReservationModificationNotice';
import { reservationModificationPolicy, reservationCancellationReason, reservationModificationMessage } from '../../../core/reservations/reservationModificationPolicy';
import { normalizeReservationBusinessRules } from '../../../core/reservations/reservationBusinessRules';
import { RESERVATION_PARTY_INPUT_MAXIMUM } from '../../../core/reservations/reservationGroupPolicy';
import ReservationGroupNotice from './ReservationGroupNotice';
import { reservationGroupContactReason, reservationGroupContactMessage, reservationGroupTerms } from '../../../core/reservations/reservationGroupPolicy';
import { readReservationTermsSnapshot } from '../../../core/reservations/reservationBusinessRules';
import { reservationTimeLimitsSummary } from '../../../core/reservations/reservationTimeLimits';
import { buildGuestModificationInterval, isOvernightBookableWindow } from '../../../core/reservations/guestModificationTime';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Unit, Booking, PublicBookingDTO, ReservationSetting } from '../../../core/models/data';
import type { GuestModificationRejectionPolicy, GuestModifyReservationResponse } from '../../../core/services/guestReservationService';
import { db } from '../../../core/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { translations } from '../../../lib/i18n';
import {
  buildReservationTheme,
  defaultThemeSettings,
  syncThemeCssVariables,
} from '../../../core/ui/reservationTheme';
import PublicReservationLayout from './PublicReservationLayout';
import { resolvePublicBookingBackgroundUrl } from '../../../core/reservations/publicBookingBackground';
import { normalizeSubmitError, type SubmitError } from '../../utils/normalizeSubmitError';
import { getOnlineStatus } from '../../utils/getOnlineStatus';
import { logTokenPresence } from '../../utils/logTokenPresence';
import { v4 as uuidv4 } from 'uuid';
import { parseManageReservationParams } from './manageReservationParams';
import { buildPublicReservationWatermark, resolvePublicBusinessName } from './publicBusinessIdentity';

import { manageGlassStyle, manageSurfaceStyle, opaqueColor, readableColor } from '../../../core/ui/manageReservationContrast';

type Locale = 'hu' | 'en';

const FUNCTIONS_BASE_URL =
  import.meta.env.VITE_FUNCTIONS_BASE_URL ||
  'https://europe-west3-mintleaf-74d27.cloudfunctions.net';

const MIN_HEADCOUNT = 1;
const MAX_HEADCOUNT = RESERVATION_PARTY_INPUT_MAXIMUM;

const PlayfulBubbles = () => (
  <>
    <div className="pointer-events-none absolute w-64 h-64 bg-white/40 blur-3xl rounded-full -top-10 -left-10" />
    <div className="pointer-events-none absolute w-52 h-52 bg-white/30 blur-2xl rounded-full top-20 right-10" />
    <div className="pointer-events-none absolute w-40 h-40 bg-white/25 blur-2xl rounded-full bottom-10 left-1/4" />
  </>
);

interface ManageReservationPageProps {
  unitId: string;
  reservationId: string;
  manageToken: string;
}

const ManageReservationPage: React.FC<ManageReservationPageProps> = ({
  unitId: _unitId,
  reservationId: _reservationId,
  manageToken: _manageToken,
}) => {
  const parsedParams = useMemo(
    () => parseManageReservationParams(window.location.search),
    []
  );
  const urlReservationId = parsedParams.reservationId;
  const urlUnitId = parsedParams.unitId;
  const urlToken = parsedParams.manageToken;
  const adminToken = parsedParams.adminToken;
  const modificationId = parsedParams.modificationId;
  const action = parsedParams.action;
  const isGuestModificationDecisionLink = Boolean(adminToken && modificationId && action);
  const isInitialAdminDecisionLink = Boolean(adminToken && action && !modificationId);
  const [booking, setBooking] = useState<PublicBookingDTO | null>(null);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [settings, setSettings] = useState<ReservationSetting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locale, setLocale] = useState<Locale>('hu');
  const [actionState, setActionState] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [actionError, setActionError] = useState<SubmitError | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [adminAction, setAdminAction] = useState<'approve' | 'reject' | null>(action);

  useEffect(() => {
    setAdminAction(action);
  }, [action]);
  const [actionMessage, setActionMessage] = useState('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [modifyHeadcount, setModifyHeadcount] = useState('');
  const [modifyStartTime, setModifyStartTime] = useState('');
  const [modifyEndTime, setModifyEndTime] = useState('');
  const [rejectionPolicy] = useState<GuestModificationRejectionPolicy>('keep_original');
  const [editTime, setEditTime] = useState(false);
  const [modifyError, setModifyError] = useState('');
  const [groupContactRequired, setGroupContactRequired] = useState(false);
  useEffect(() => setGroupContactRequired(false), [modifyHeadcount, modifyStartTime, modifyEndTime]);
  const [modifySuccess, setModifySuccess] = useState('');
  const [isSavingModification, setIsSavingModification] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const actionErrorRef = useRef<HTMLDivElement | null>(null);


  const reservationTimeZone =
    (settings as any)?.timezone || (settings as any)?.timeZone || 'Europe/Budapest';

  const formatReservationTimeForInput = (dateValue: Date | null, timeZone: string) => {
    if (!dateValue) return '';
    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(dateValue);
  };

  const theme = useMemo(() => {
    const persistedTheme = settings?.theme || defaultThemeSettings;
    return buildReservationTheme({
      ...persistedTheme,
      backgroundImageUrl: resolvePublicBookingBackgroundUrl(
        settings?.publicBackgroundImageUrl,
        persistedTheme.backgroundImageUrl,
      ),
    }, settings?.uiTheme);
  }, [settings]);

  const isMinimalGlassTheme = theme.uiTheme === 'minimal_glass';
  const panelStyle = isMinimalGlassTheme
    ? manageGlassStyle('panel', theme.colors)
    : manageSurfaceStyle(theme.colors.background, theme.colors);
  const surfaceStyle = isMinimalGlassTheme
    ? manageGlassStyle('surface', theme.colors)
    : manageSurfaceStyle(theme.colors.surface, theme.colors);
  const outerStyle = isMinimalGlassTheme
    ? manageGlassStyle('outer', theme.colors)
    : manageSurfaceStyle(theme.colors.surface, theme.colors);
  const panelBorder = isMinimalGlassTheme ? 'rgba(255,255,255,0.14)' : theme.colors.surface;
  const manageTheme = {
    ...theme,
    cardStyle: { ...theme.cardStyle, ...outerStyle },
  };
  const t = translations[locale];
  const isSubmittingAction = actionState === 'submitting';

  useEffect(() => {
    console.info('manage reservation params', {
      hasUnitId: Boolean(urlUnitId),
      hasReservationId: Boolean(urlReservationId),
      hasManageToken: Boolean(urlToken),
      hasAdminToken: Boolean(adminToken),
      hasModificationId: Boolean(modificationId),
      action,
    });
  }, [action, adminToken, modificationId, urlReservationId, urlToken, urlUnitId]);

  useEffect(() => {
    if (actionError && actionErrorRef.current) {
      actionErrorRef.current.focus();
    }
  }, [actionError]);

  const createTraceId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : uuidv4();

  const reportActionError = (
    traceId: string,
    context: { action: string },
    errorInput: Parameters<typeof normalizeSubmitError>[0]
  ) => {
    const normalized = normalizeSubmitError({
      ...errorInput,
      traceId,
      isOnline: getOnlineStatus(),
      locale,
    });
    setActionError(normalized);
    setActionState('error');
    console.log(
      `[manage-action] error traceId=${traceId} kind=${normalized.kind} message=${normalized.userMessage} action=${context.action}`
    );
  };


  const buildPublicUnit = (
    settingsValue: ReservationSetting | null
  ): Unit => {
    const settingsAny = settingsValue as Record<string, any> | null;
    const name = resolvePublicBusinessName(settingsAny);
    const logoUrl =
      settingsValue?.theme?.headerLogoUrl || settingsValue?.theme?.timeWindowLogoUrl;
    return {
      id: urlUnitId,
      name,
      logoUrl,
    };
  };

  useEffect(() => {
    if (!urlUnitId) return;
    const fetchSettings = async () => {
      try {
        const settingsSnap = await getPublicReservationSettings(urlUnitId);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data() as ReservationSetting;
          const nextSettings: ReservationSetting = {
            ...data,
            blackoutDates: data.blackoutDates || [],
            id: urlUnitId,
            uiTheme: data.uiTheme || 'minimal_glass',
            theme: {
              ...defaultThemeSettings,
              ...(data.theme || {}),
            },
          };
          setSettings(nextSettings);
          setUnit(prev => prev ?? buildPublicUnit(nextSettings));
          return;
        }
        const fallbackSettings: ReservationSetting = {
          id: urlUnitId,
          blackoutDates: [],
          uiTheme: 'minimal_glass',
          theme: defaultThemeSettings,
        } as ReservationSetting;
        setSettings(fallbackSettings);
        setUnit(prev => prev ?? buildPublicUnit(fallbackSettings));
      } catch (settingsErr) {
        console.error('Error fetching reservation settings:', settingsErr);
        const fallbackSettings: ReservationSetting = {
          id: urlUnitId,
          blackoutDates: [],
          uiTheme: 'minimal_glass',
          theme: defaultThemeSettings,
        } as ReservationSetting;
        setSettings(fallbackSettings);
        setUnit(prev => prev ?? buildPublicUnit(fallbackSettings));
      }
    };

    fetchSettings();
  }, [urlUnitId]);

  const isAdminTokenExpired = (bookingRecord: PublicBookingDTO | null) => {
    if (!bookingRecord?.adminActionExpiresAtMs) return false;
    return bookingRecord.adminActionExpiresAtMs < Date.now();
  };

  const isAdminTokenUsed = (bookingRecord: PublicBookingDTO | null) =>
    !!bookingRecord?.adminActionUsedAtMs;

  const isConfirmedOrCompleted = booking?.status === 'confirmed';
  const hasPendingGuestModification = Boolean(
    booking?.hasPendingGuestModification || booking?.pendingGuestModification?.status === 'pending'
  );
  const decreaseRequiresApproval = (() => { try { return normalizeReservationBusinessRules(settings?.businessRules).modifications.decreaseRequiresApproval; } catch { return true; } })();
  const isModificationRequestMode = Boolean(!hasPendingGuestModification && (Number(modifyHeadcount) > (booking?.headcount ?? 0) || (Number(modifyHeadcount) < (booking?.headcount ?? 0) && decreaseRequiresApproval) || (editTime && isConfirmedOrCompleted)));
  const isDirectPendingModifyMode = booking?.status === 'pending';
  const isModifyLocked =
    !booking?.startTimeMs ||
    booking?.status === 'cancelled' ||
    hasPendingGuestModification ||
    (!isDirectPendingModifyMode && !isModificationRequestMode);
  const showAdminSection = !!adminToken;
  const isAdminActionAvailable =
    showAdminSection &&
    booking?.status === 'pending' &&
    !isAdminTokenExpired(booking) &&
    !isAdminTokenUsed(booking);
  const adminUnavailableMessage =
    booking?.status !== 'pending'
      ? t.adminActionNotPending
      : isAdminTokenExpired(booking)
      ? t.expiredAdminToken
      : t.invalidAdminToken;

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);
      try {
        if (isGuestModificationDecisionLink) {
          const decisionResponse = await fetch(`${FUNCTIONS_BASE_URL}/adminHandleGuestModificationRequest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              unitId: urlUnitId,
              reservationId: urlReservationId,
              modificationId,
              adminToken,
              action,
            }),
          });
          if (decisionResponse.ok === false) {
            const payload = await decisionResponse.json().catch(() => ({}));
            const normalized = normalizeSubmitError({
              response: decisionResponse,
              payload,
              isOnline: getOnlineStatus(),
              locale,
            });
            setError(`${normalized.userTitle}: ${normalized.userMessage}`);
            return;
          }
          setUnit(prev => prev ?? ({ id: urlUnitId, name: 'MintLeaf' } as Unit));
          setActionMessage(action === 'approve' ? t.modificationRequestApproved : t.modificationRequestRejected);
          setActionState('success');
          setBooking(null);
          return;
        }

        const response = await fetch(
          `${FUNCTIONS_BASE_URL}/guestGetReservation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(
              adminToken
                ? {
                    unitId: urlUnitId,
                    reservationId: urlReservationId,
                    adminToken,
                  }
                : {
                    unitId: urlUnitId,
                    reservationId: urlReservationId,
                    manageToken: urlToken,
                  }
            ),
          }
        );

        if (response.ok === false) {
          if (response.status === 410) {
            setError(adminToken ? t.expiredAdminToken : t.expiredManageLink);
            return;
          }
          if (response.status === 403 || response.status === 404) {
            setError(adminToken ? t.invalidAdminToken : t.invalidManageLink);
            return;
          }
          throw new Error('FETCH_FAILED');
        }

        const payload = await response.json();
        const foundBooking: PublicBookingDTO = {
          id: payload.id,
          unitId: payload.unitId,
          unitName: payload.unitName,
          termsSnapshot: readReservationTermsSnapshot(payload.termsSnapshot, payload.unitId),
          name: payload.name,
          headcount: payload.headcount,
          startTimeMs: payload.startTimeMs ?? null,
          endTimeMs: payload.endTimeMs ?? null,
          preferredTimeSlot: payload.preferredTimeSlot ?? null,
          seatingPreference: payload.seatingPreference ?? 'any',
          status: payload.status,
          occasion: payload.occasion || '',
          source: payload.source || '',
          locale: payload.locale || 'hu',
          referenceCode: payload.referenceCode,
          cancelReason: payload.cancelReason || '',
          cancelledBy: payload.cancelledBy || undefined,
          contact: payload.contact || { phoneE164: '', email: '' },
          adminActionExpiresAtMs: payload.adminActionExpiresAtMs ?? null,
          adminActionUsedAtMs: payload.adminActionUsedAtMs ?? null,
          hasPendingGuestModification: payload.hasPendingGuestModification ?? false,
          pendingGuestModification: payload.pendingGuestModification ?? null,
          cancellationReasonCode: payload.cancellationReasonCode || undefined,
        };

        setBooking(foundBooking);
        setUnit(prev =>
          prev ??
          ({
            id: payload.unitId,
            // Temporary backend payload compatibility: payload.unitName is expected to be public-display-safe.
            name: resolvePublicBusinessName({ unitName: payload.unitName }, { allowLegacyUnitName: true }),
          } as Unit)
        );

        const urlParams = new URLSearchParams(window.location.search);
        const langOverride = urlParams.get('lang');
        if (langOverride === 'en' || langOverride === 'hu') {
          setLocale(langOverride);
        } else {
          setLocale(foundBooking.locale || 'hu');
        }
      } catch (err: any) {
        console.error('Error fetching reservation:', err);
        setError(t.actionFailed);
      } finally {
        setLoading(false);
      }
    };

    if (urlUnitId && urlReservationId && (urlToken || adminToken)) {
      fetchBooking();
    } else {
      setError('Érvénytelen vagy hiányzó módosító token.');
      setLoading(false);
    }
  }, [action, adminToken, isGuestModificationDecisionLink, modificationId, t.actionFailed, t.errorCapacityFull, t.expiredAdminToken, t.expiredManageLink, t.invalidAdminToken, t.invalidManageLink, t.modificationRequestApproved, t.modificationRequestRejected, urlReservationId, urlToken, urlUnitId]);

  useEffect(() => {
    syncThemeCssVariables(theme);
  }, [theme]);



  useEffect(() => {
    if (!booking) return;
    const startDate = booking.startTimeMs ? new Date(booking.startTimeMs) : null;
    const endDate = booking.endTimeMs ? new Date(booking.endTimeMs) : null;
    setModifyHeadcount(String(booking.headcount ?? ''));
    setModifyStartTime(formatReservationTimeForInput(startDate, reservationTimeZone));
    setModifyEndTime(formatReservationTimeForInput(endDate, reservationTimeZone));
    setModifyError('');
    setModifySuccess('');
    setEditTime(false);
  }, [booking]);

  const writeDecisionLog = async (status: 'confirmed' | 'cancelled') => {
    // Decision logs are written by the backend after token validation.
    void status;
  };

  const handleAdminDecision = async (decision: 'approve' | 'reject') => {
    if (!booking || !unit) return;
    if (isSubmittingAction) return;
    const traceId = createTraceId();
    const tokenPresence = logTokenPresence(adminToken);
    console.log(
      `[manage-action] start traceId=${traceId} token=${tokenPresence} action=${decision}`
    );
    if (!adminToken || isAdminTokenExpired(booking) || isAdminTokenUsed(booking)) {
      reportActionError(
        traceId,
        { action: decision },
        { error: new Error('invalid token') }
      );
      return;
    }
    setIsProcessingAction(true);
    setActionError(null);
    setActionState('submitting');
    try {
      const nextStatus = decision === 'approve' ? 'confirmed' : 'cancelled';
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/adminHandleReservationAction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            unitId: unit.id,
            reservationId: booking.id,
            adminToken,
            action: decision,
          }),
        }
      );

      if (response.ok === false) {
        reportActionError(
          traceId,
          { action: decision },
          { response }
        );
        return;
      }
      await writeDecisionLog(nextStatus);

      // !!! nincs FE email küldés, backend intézi !!!

      setBooking(prev =>
        prev
          ? {
              ...prev,
              status: nextStatus,
              adminActionUsedAtMs: Date.now(),
              cancelledBy: nextStatus === 'cancelled' ? 'admin' : prev.cancelledBy,
            }
          : null
      );
      setActionMessage(
        decision === 'approve' ? t.reservationApproved : t.reservationRejected
      );
      setActionState('success');
      console.log(`[manage-action] success traceId=${traceId} action=${decision}`);
    } catch (actionErr) {
      console.error('Error handling admin decision:', actionErr);
      reportActionError(
        traceId,
        { action: decision },
        { error: actionErr }
      );
    } finally {
      setIsProcessingAction(false);
      setActionState((prev) => (prev === 'submitting' ? 'idle' : prev));
    }
  };

  const handleCancelReservation = async () => {
    if (!booking || !unit) return;
    if (isSubmittingAction) return;
    const traceId = createTraceId();
    const tokenPresence = logTokenPresence(urlToken);
    console.log(
      `[manage-action] start traceId=${traceId} token=${tokenPresence} action=cancel`
    );
    if (!urlToken) {
      reportActionError(
        traceId,
        { action: 'cancel' },
        { error: new Error('invalid link') }
      );
      return;
    }
    const cancellationReason = reservationCancellationReason(settings ?? {}, new Date(booking.startTimeMs!), Date.now());
    if (cancellationReason) { setCancelError(reservationModificationMessage(cancellationReason, locale)); return; }
    setIsCancelling(true);
    setCancelError('');
    setActionError(null);
    setActionState('submitting');
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/guestUpdateReservation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            unitId: unit.id,
            reservationId: booking.id,
            manageToken: urlToken,
            action: 'cancel',
            reason: cancelReason.trim(),
          }),
        }
      );

      if (response.ok === false) {
        reportActionError(
          traceId,
          { action: 'cancel' },
          { response }
        );
        return;
      }

      // !!! nincs FE email küldés, backend intézi !!!

      setBooking(prev =>
        prev
          ? {
              ...prev,
              status: 'cancelled',
              cancelReason: cancelReason.trim(),
              cancelledBy: 'guest',
            }
          : null
      );
      setIsCancelModalOpen(false);
      setCancelReason('');
      setActionState('success');
      console.log(`[manage-action] success traceId=${traceId} action=cancel`);
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      reportActionError(
        traceId,
        { action: 'cancel' },
        { error: err }
      );
    } finally {
      setIsCancelling(false);
      setActionState((prev) => (prev === 'submitting' ? 'idle' : prev));
    }
  };

  const snapToQuarterHour = (date: Date) => {
    const roundedMinutes = Math.floor(date.getMinutes() / 15) * 15;
    const snapped = new Date(date);
    snapped.setMinutes(roundedMinutes, 0, 0);
    return snapped;
  };

  const handleModifyReservation = async () => {
    if (!booking || !unit) return;
    if (isSubmittingAction) return;
    const traceId = createTraceId();
    const tokenPresence = logTokenPresence(urlToken);
    console.log(
      `[manage-action] start traceId=${traceId} token=${tokenPresence} action=modify`
    );
    if (!urlToken) {
      reportActionError(
        traceId,
        { action: 'modify' },
        { error: new Error('invalid link') }
      );
      return;
    }
    setModifyError('');
    setModifySuccess('');
    setActionError(null);

    const nextHeadcount = Number(modifyHeadcount);
    if (!Number.isSafeInteger(nextHeadcount)) {
      setModifyError(t.modifyInvalidHeadcount);
      return;
    }
    if (nextHeadcount < MIN_HEADCOUNT || nextHeadcount > MAX_HEADCOUNT) {
      setModifyError(
        t.modifyHeadcountRange.replace('{min}', String(MIN_HEADCOUNT)).replace(
          '{max}',
          String(MAX_HEADCOUNT)
        )
      );
      return;
    }
    if (hasPendingGuestModification) {
      setModifyError(t.pendingGuestModificationExists);
      return;
    }
    if (isModificationRequestMode && !rejectionPolicy) {
      setModifyError(t.modifyRequestRequiresPolicy);
      return;
    }
    if (!modifyStartTime || !modifyEndTime) {
      setModifyError(t.modifyMissingTime);
      return;
    }
    if (!booking.startTimeMs) {
      setModifyError(t.actionFailed);
      return;
    }
    let nextStart: Date;
    let nextEnd: Date;
    try {
      const interval = editTime ? buildGuestModificationInterval({
        baseDate: new Date(booking.startTimeMs), startTime: modifyStartTime,
        endTime: modifyEndTime, timeZone: reservationTimeZone,
        bookableWindow: settings?.bookableWindow,
      }) : { start: new Date(booking.startTimeMs), end: new Date(booking.endTimeMs!) };
      nextStart = editTime ? snapToQuarterHour(interval.start) : interval.start;
      nextEnd = editTime ? snapToQuarterHour(interval.end) : interval.end;
    } catch {
      setModifyError(t.errorTime);
      return;
    }
    setModifyStartTime(formatReservationTimeForInput(nextStart, reservationTimeZone));
    setModifyEndTime(formatReservationTimeForInput(nextEnd, reservationTimeZone));
    if (nextEnd.getTime() <= nextStart.getTime()) {
      setModifyError(t.errorTime);
      return;
    }

    const policy = reservationModificationPolicy(settings ?? {}, { originalHeadcount: booking.headcount, headcount: nextHeadcount, originalStart: new Date(booking.startTimeMs), originalEnd: new Date(booking.endTimeMs!), start: nextStart, end: nextEnd, nowMs: Date.now(), status: booking.status });
    if (policy.reason) { setModifyError(reservationModificationMessage(policy.reason, locale)); return; }
    setGroupContactRequired(false);
    if (nextHeadcount > booking.headcount || nextStart.getTime() !== booking.startTimeMs || nextEnd.getTime() !== booking.endTimeMs) {
      const groupReason = reservationGroupContactReason(settings ?? {}, nextHeadcount);
      if (groupReason) { setGroupContactRequired(true); setModifyError(reservationGroupContactMessage(groupReason, locale)); return; }
    }
    setActionState('submitting');
    setIsSavingModification(true);
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/guestModifyReservation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            unitId: unit.id,
            reservationId: booking.id,
            manageToken: urlToken,
            headcount: nextHeadcount,
            startTimeMs: nextStart.getTime(),
            endTimeMs: nextEnd.getTime(),
            rejectionPolicy: 'keep_original',
            expectedOriginal: { headcount: booking.headcount, startTimeMs: booking.startTimeMs, endTimeMs: booking.endTimeMs },
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (response.ok === false) {
        const normalized = normalizeSubmitError({
          response,
          payload,
          traceId,
          isOnline: getOnlineStatus(),
          locale,
        });
        if (normalized.kind === 'capacity') {
          setModifyError(normalized.userMessage);
          setActionState((prev) => (prev === 'submitting' ? 'idle' : prev));
          return;
        }
        if (payload?.error === 'group_contact_required') setGroupContactRequired(true);
        reportActionError(
          traceId,
          { action: 'modify' },
          { response, payload }
        );
        return;
      }

      const modifyPayload: GuestModifyReservationResponse = payload;
      if (modifyPayload.mode === 'pending_modification_request') {
        setBooking(prev =>
          prev
            ? {
                ...prev,
                hasPendingGuestModification: true,
                pendingGuestModification: {
                  id: modifyPayload.pendingModificationId || 'pending',
                  status: 'pending',
                  requestedAtMs: Date.now(),
                  requestedBy: 'guest',
                  source: 'guestManageLink',
                  rejectionPolicy: rejectionPolicy || null,
                  modifiedFields: [],
                  original: {
                    status: prev.status,
                    headcount: prev.headcount,
                    startTimeMs: prev.startTimeMs,
                    endTimeMs: prev.endTimeMs,
                  },
                  proposed: {
                    headcount: nextHeadcount,
                    startTimeMs: nextStart.getTime(),
                    endTimeMs: nextEnd.getTime(),
                  },
                },
              }
            : null
        );
        setModifySuccess(t.modifyRequestSuccess);
      } else {
        setBooking(prev =>
          prev
            ? {
                ...prev,
                headcount: modifyPayload.headcount ?? nextHeadcount,
                startTimeMs: modifyPayload.startTimeMs ?? nextStart.getTime(),
                endTimeMs: modifyPayload.endTimeMs ?? nextEnd.getTime(),
              }
            : null
        );
        setModifySuccess(t.modifySuccess);
      }
      setActionState('success');
      console.log(`[manage-action] success traceId=${traceId} action=modify`);
    } catch (err) {
      console.error('Error modifying reservation:', err);
      reportActionError(
        traceId,
        { action: 'modify' },
        { error: err }
      );
    } finally {
      setIsSavingModification(false);
      setActionState((prev) => (prev === 'submitting' ? 'idle' : prev));
    }
  };

  const baseButtonClasses = useMemo(
    () => ({
      primary: theme.styles.primaryButton,
      secondary: theme.styles.secondaryButton,
      outline: theme.styles.outlineButton,
    }),
    [theme.styles.outlineButton, theme.styles.primaryButton, theme.styles.secondaryButton]
  );

  const decorations = theme.uiTheme === 'playful_bubble' ? <PlayfulBubbles /> : undefined;
  const watermarkText = buildPublicReservationWatermark(unit?.name);

  const baseLayoutProps = {
    theme: manageTheme,
    isMinimalGlassTheme,
    decorations,
    watermarkText,
  };

  useEffect(() => {
    if (
      booking &&
      booking.status === 'pending' &&
      isInitialAdminDecisionLink &&
      adminAction &&
      adminToken &&
      !isAdminTokenExpired(booking) &&
      !isAdminTokenUsed(booking)
    ) {
      handleAdminDecision(adminAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking, adminAction, adminToken, isInitialAdminDecisionLink]);

  if (loading)
    return (
      <PublicReservationLayout
        {...baseLayoutProps}
        header={<LoadingSpinner />}
        body={<div />}
      />
    );
  if (error)
    return (
      <PublicReservationLayout
        {...baseLayoutProps}
        header={
          <h2
            className={`text-xl font-bold text-red-600 ${
              isMinimalGlassTheme ? 'text-[var(--color-text-primary)]' : ''
            }`}
          >
            Hiba
          </h2>
        }
        body={
          <p
            className={`mt-2 ${
              isMinimalGlassTheme ? 'text-[var(--color-text-secondary)]' : 'text-current'
            }`}
          >
            {error}
          </p>
        }
      />
    );
  if (!booking && actionMessage) {
    return (
      <PublicReservationLayout
        {...baseLayoutProps}
        header={<h2 className="text-xl font-bold">{t.manageTitle}</h2>}
        body={<p className="mt-2">{actionMessage}</p>}
      />
    );
  }
  if (!booking || !unit) return null;

  const getStatusChip = (status: Booking['status']) => {
    const text = (t as any)[`status_${status}`] || status;
    const backgroundColor = opaqueColor(theme.colors.background);
    const textColor = readableColor(backgroundColor,
      status === 'pending' ? theme.colors.accent : status === 'confirmed' ? theme.colors.primary : theme.colors.danger);
    return (
      <span
        className={`px-3 py-1 text-sm font-bold rounded-full ${theme.radiusClass}`}
        style={isMinimalGlassTheme
          ? { ...surfaceStyle, color: `var(--manage-${status === 'pending' ? 'accent' : status === 'confirmed' ? 'primary' : 'danger'})` }
          : { backgroundColor, color: textColor }}
        title={`${t.status}: ${text}`}
        aria-label={`${t.status}: ${text}`}
      >
        {text}
      </span>
    );
  };

  const maskPhone = (phoneE164: string): string => {
    if (!phoneE164 || phoneE164.length < 10) return phoneE164;
    const last4 = phoneE164.slice(-4);
    return phoneE164.slice(0, -7) + '••• •' + last4;
  };

  const getStartDate = () =>
    booking?.startTimeMs ? new Date(booking.startTimeMs) : null;
  const getEndDate = () =>
    booking?.endTimeMs ? new Date(booking.endTimeMs) : null;
  const getCancelledByLabel = () => {
    if (!booking?.cancelledBy) return '';
    const fullKey = `cancelledBy_${booking.cancelledBy}_label`;
    const key = `cancelledBy_${booking.cancelledBy}`;
    return (t as any)[fullKey] || (t as any)[key] || booking.cancelledBy;
  };

  const formatGuestModificationDateTime = (valueMs?: number | null) =>
    valueMs
      ? new Date(valueMs).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
      : '—';
  const renderPendingModificationDiff = (label: string, originalValue: string, proposedValue: string) => {
    const changed = originalValue !== proposedValue;
    return (
      <p>
        <strong>{label}:</strong>{' '}
        {changed ? (
          <>
            <s>{originalValue}</s>
            <span className="mx-1">→</span>
            <span className="font-semibold">{proposedValue}</span>
          </>
        ) : (
          <span>{originalValue}</span>
        )}
      </p>
    );
  };

  const getRejectionPolicyLabel = (policy?: GuestModificationRejectionPolicy | null) =>
    policy === 'cancel_original' ? t.rejectionPolicyCancelOriginal : t.rejectionPolicyKeepOriginal;

  const pendingModificationSummary = booking.pendingGuestModification;

  const headerSection = (
    <>
      <h1
        className={`text-4xl font-bold ${
          isMinimalGlassTheme ? 'text-[var(--color-text-primary)]' : ''
        }`}
        style={{ color: 'var(--manage-text-primary)' }}
      >
        {unit.name}
      </h1>
      <p
        className={`text-lg mt-1 ${
          isMinimalGlassTheme ? 'text-[var(--color-text-secondary)]' : ''
        }`}
        style={{ color: 'var(--manage-text-secondary)' }}
      >
        {t.manageTitle}
      </p>
    </>
  );

  const bodySection = (
    <div className="flex flex-col gap-6 min-h-full" style={{ color: 'var(--manage-text-primary)' }}>
      {actionError && (
        <div
          ref={actionErrorRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          className="p-4 bg-red-100 text-red-800 font-semibold rounded-lg text-sm leading-relaxed break-words border border-red-200 shadow-sm flex flex-col gap-3"
        >
          <div>
            <p className="text-base font-bold">{actionError.userTitle}</p>
            <p className="mt-1">{actionError.userMessage}</p>
          </div>
          {actionError.debugId && (
            <p className="text-xs font-medium">
              {locale === 'hu' ? 'Hiba azonosító' : 'Error ID'}: {actionError.debugId}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              setActionError(null);
              setActionState('idle');
            }}
            className={`${baseButtonClasses.primary} ${theme.radiusClass} text-sm`}
            style={{ backgroundColor: opaqueColor(theme.colors.primary, '#166534'), color: readableColor(opaqueColor(theme.colors.primary, '#166534'), '#ffffff') }}
          >
            {locale === 'hu' ? 'Próbáld újra' : 'Try again'}
          </button>
        </div>
      )}
      <div
        className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pb-4 border-b"
        style={{ borderColor: isMinimalGlassTheme ? panelBorder : `${theme.colors.surface}60` }}
      >
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--manage-text-primary)' }}>
          {t.reservationDetails}
        </h2>
        {getStatusChip(booking.status)}
      </div>

      {reservationTimeLimitsSummary(settings ?? {}, locale) && <p className="rounded-lg border p-4 text-sm">{locale === 'hu' ? 'Időpontváltásra érvényes: ' : 'For time changes: '}{reservationTimeLimitsSummary(settings ?? {}, locale)}</p>}
      {booking.termsSnapshot && reservationGroupTerms({businessRules:booking.termsSnapshot.rules}, booking.headcount, locale, booking.termsSnapshot.promisedGraceMinutes) && <p className="rounded-lg border p-4 text-sm">{reservationGroupTerms({businessRules:booking.termsSnapshot.rules}, booking.headcount, locale, booking.termsSnapshot.promisedGraceMinutes)}</p>}
      {settings && (Number(modifyHeadcount) > booking.headcount || groupContactRequired) && <ReservationGroupNotice settings={settings} headcount={Number(modifyHeadcount || booking.headcount)} locale={locale} forceContact={groupContactRequired} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {booking.termsSnapshot && (booking.termsSnapshot.rules.terms[locale] || booking.termsSnapshot.rules.terms.hu) && <p className="whitespace-pre-wrap rounded-lg border p-4 lg:col-span-2">{booking.termsSnapshot.rules.terms[locale] || booking.termsSnapshot.rules.terms.hu}</p>}
        <div className="space-y-4">
          <div
            className={`p-4 border ${theme.radiusClass}`}
            style={{
              ...panelStyle,
              borderColor: panelBorder,
              color: 'var(--manage-text-primary)',
            }}
          >
            <div className="space-y-3">
              <p>
                <strong>{t.status}:</strong> <span className="ml-2">{getStatusChip(booking.status)}</span>
              </p>
              <p>
                <strong>{t.referenceCode}:</strong>{' '}
                <span
                  className={`font-mono px-2 py-1 text-sm ${theme.radiusClass}`}
                  style={{
                    ...surfaceStyle,
                    color: 'var(--manage-text-primary)',
                    border: `1px solid ${panelBorder}`,
                  }}
                >
                  {booking.referenceCode?.substring(0, 8).toUpperCase()}
                </span>
              </p>
              <p>
                <strong>{t.name}:</strong> {booking.name}
              </p>
              <p>
                <strong>{t.headcount}:</strong> {booking.headcount}
              </p>
              <p>
                <strong>{t.date}:</strong>{' '}
                {getStartDate()
                  ? getStartDate()!.toLocaleDateString(locale, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '—'}
              </p>
              <p>
                <strong>{t.startTime}:</strong>{' '}
                {getStartDate()
                  ? getStartDate()!.toLocaleTimeString(locale, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
              <p>
                <strong>{t.endTime}:</strong>{' '}
                {getEndDate()
                  ? getEndDate()!.toLocaleTimeString(locale, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
              <p>
                <strong>{t.email}:</strong> {booking.contact?.email}
              </p>
              <p>
                <strong>{t.phone}:</strong>{' '}
                {booking.contact?.phoneE164 ? maskPhone(booking.contact.phoneE164) : 'N/A'}
              </p>
              <p>
                <strong>{t.preferredTimeSlotLabel}:</strong>{' '}
                {booking.preferredTimeSlot || t.preferenceNotProvided}
              </p>
              <p>
                <strong>{t.seatingPreferenceLabel}:</strong>{' '}
                {booking.seatingPreference && booking.seatingPreference !== 'any'
                  ? ({
                      bar: t.seatingPreferenceBar,
                      table: t.seatingPreferenceTable,
                      outdoor: t.seatingPreferenceOutdoor,
                    } as Record<string, string>)[booking.seatingPreference] ||
                    t.preferenceNotProvided
                  : t.preferenceNotProvided}
              </p>
              {booking.cancelledBy && (
                <p>
                  <strong>{t.cancelledByLabel}:</strong> {getCancelledByLabel()}
                </p>
              )}
              {booking.cancelReason && (
                <p>
                  <strong>{t.cancelReasonLabel}:</strong> {booking.cancelReason}
                </p>
              )}
              {booking.cancellationReasonCode === 'guest_modification_rejected_cancel_original' && (
                <p className="text-sm" style={{ color: 'var(--manage-text-secondary)' }}>
                  {t.guestModificationRejectedCancelOriginalReason}
                </p>
              )}
            </div>
          </div>

          {pendingModificationSummary && (
            <div
              className={`p-4 border ${theme.radiusClass}`}
              style={{
                ...panelStyle,
                borderColor: isMinimalGlassTheme ? panelBorder : `${theme.colors.accent}50`,
                color: 'var(--manage-text-primary)',
              }}
            >
              <h3 className="text-lg font-semibold">{t.pendingGuestModificationTitle}</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--manage-text-secondary)' }}>
                {t.pendingGuestModificationExists}
              </p>
              <div className={`mt-3 p-3 border ${theme.radiusClass} text-sm`} style={{ borderColor: panelBorder }}>
                <p className="font-semibold">{t.pendingGuestModificationOriginal} → {t.pendingGuestModificationProposed}</p>
                {renderPendingModificationDiff(t.headcount, `${pendingModificationSummary.original.headcount ?? '—'} fő`, `${pendingModificationSummary.proposed.headcount ?? '—'} fő`)}
                {renderPendingModificationDiff(t.startTime, formatGuestModificationDateTime(pendingModificationSummary.original.startTimeMs), formatGuestModificationDateTime(pendingModificationSummary.proposed.startTimeMs))}
                {renderPendingModificationDiff(t.endTime, formatGuestModificationDateTime(pendingModificationSummary.original.endTimeMs), formatGuestModificationDateTime(pendingModificationSummary.proposed.endTimeMs))}
              </div>
              <p className="text-sm mt-3">
                <strong>{t.rejectionPolicyLabel}:</strong>{' '}
                {getRejectionPolicyLabel(pendingModificationSummary.rejectionPolicy)}
              </p>
            </div>
          )}

          {booking.status === 'pending' && (
            <div
              className={`p-4 ${theme.radiusClass} border`}
              style={{
                ...panelStyle,
                color: 'var(--manage-text-primary)',
                borderColor: panelBorder,
              }}
            >
              <p className="font-semibold" style={{ color: 'var(--manage-text-primary)' }}>
                {t.pendingApproval}
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--manage-text-secondary)' }}>
                {t.pendingApprovalHint}
              </p>
            </div>
          )}

          {showAdminSection && (
            <div className="space-y-2">
              <div className="text-sm font-semibold" style={{ color: 'var(--manage-text-primary)' }}>
                {t.adminActionsLabel}
              </div>
              <details
                className={`border ${theme.radiusClass} p-4 group`}
                style={{
                  ...panelStyle,
                  color: 'var(--manage-text-primary)',
                  borderColor: isMinimalGlassTheme ? panelBorder : `${theme.colors.accent}40`,
                }}
              >
                <summary
                  className="flex items-center justify-between cursor-pointer font-semibold list-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                  style={{ color: 'var(--manage-text-primary)' }}
                >
                  <span>{t.adminActionTitle}</span>
                  <span
                    aria-hidden="true"
                    className="text-xs transition-transform group-open:rotate-180"
                  >
                    ▾
                  </span>
                </summary>
                <div className="mt-3 space-y-3">
                  {!isAdminActionAvailable && (
                    <p className="text-sm" style={{ color: 'var(--manage-text-secondary)' }}>
                      {adminUnavailableMessage}
                    </p>
                  )}
                  {isAdminActionAvailable && actionMessage && (
                    <p
                      className={`text-sm p-2 ${theme.radiusClass} border`}
                      style={{
                        ...surfaceStyle,
                        color: 'var(--manage-primary)',
                        borderColor: isMinimalGlassTheme ? panelBorder : `${theme.colors.primary}40`,
                      }}
                    >
                      {actionMessage}
                    </p>
                  )}
                  {isAdminActionAvailable && !actionMessage && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => handleAdminDecision('approve')}
                        className={`${baseButtonClasses.primary} flex-1`}
                        style={{ backgroundColor: opaqueColor(theme.colors.primary, '#166534'), color: readableColor(opaqueColor(theme.colors.primary, '#166534'), '#ffffff') }}
                        disabled={isProcessingAction || isSubmittingAction}
                        aria-busy={isProcessingAction || isSubmittingAction}
                        aria-label={isProcessingAction || isSubmittingAction ? 'Foglalás jóváhagyása folyamatban' : 'Foglalás jóváhagyása'}
                      >
                        {isProcessingAction || isSubmittingAction ? 'Jóváhagyás...' : t.adminApprove}
                      </button>
                      <button
                        onClick={() => handleAdminDecision('reject')}
                        className={`${baseButtonClasses.primary} flex-1`}
                        style={{ backgroundColor: opaqueColor(theme.colors.danger, '#dc2626'), color: readableColor(opaqueColor(theme.colors.danger, '#dc2626'), '#ffffff') }}
                        disabled={isProcessingAction || isSubmittingAction}
                        aria-busy={isProcessingAction || isSubmittingAction}
                        aria-label={isProcessingAction || isSubmittingAction ? 'Foglalás elutasítása folyamatban' : 'Foglalás elutasítása'}
                      >
                        {isProcessingAction || isSubmittingAction ? 'Elutasítás...' : t.adminReject}
                      </button>
                    </div>
                  )}
                </div>
              </details>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="text-sm font-semibold" style={{ color: 'var(--manage-text-primary)' }}>
              {t.guestActionsLabel}
            </div>
            <div
              className={`p-4 border ${theme.radiusClass}`}
              style={{
                ...panelStyle,
                borderColor: panelBorder,
                color: 'var(--manage-text-primary)',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-semibold">
                  {isModificationRequestMode ? t.modifyRequestSectionTitle : t.modifySectionTitle}
                </h3>
                {isModifyLocked && (
                  <span
                    className="text-xs font-semibold"
                    style={{ color: 'var(--manage-text-secondary)' }}
                  >
                    {t.modifyLocked}
                  </span>
                )}
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--manage-text-secondary)' }}>
                {isModificationRequestMode ? t.modifyRequestHint : t.modifySectionHint}
              </p>
              {booking.status === 'cancelled' && (
                <p className="text-sm mt-2" style={{ color: 'var(--manage-danger)' }}>
                  {t.reservationCancelledSuccess}
                </p>
              )}
              {hasPendingGuestModification && (
                <p className="text-sm mt-2" style={{ color: 'var(--manage-accent)' }}>
                  {t.pendingGuestModificationExists}
                </p>
              )}
              <ReservationModificationNotice settings={settings ?? {}} locale={locale} acceptedHeadcount={booking.headcount} requestedHeadcount={Number(modifyHeadcount)} requiresApproval={isModificationRequestMode} />
              <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" checked={editTime} onChange={e => setEditTime(e.target.checked)} disabled={isModifyLocked || isSavingModification} />{locale === 'hu' ? 'Az időpontot is módosítom' : 'Also change the time'}</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t.headcount}</label>
                  <input
                    type="number"
                    min={MIN_HEADCOUNT}
                    max={MAX_HEADCOUNT}
                    value={modifyHeadcount}
                    onChange={(event) => setModifyHeadcount(event.target.value)}
                    disabled={isModifyLocked || isSavingModification || isSubmittingAction}
                    className={`w-full p-3 border ${theme.radiusClass} focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
                    style={{
                      ...surfaceStyle,
                      color: 'var(--manage-text-primary)',
                      borderColor: panelBorder,
                    }}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--manage-text-secondary)' }}>
                    {t.modifyHeadcountHint
                      .replace('{min}', String(MIN_HEADCOUNT))
                      .replace('{max}', String(MAX_HEADCOUNT))}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t.startTime}</label>
                  <input
                    type="time"
                    step={900}
                    min={isOvernightBookableWindow(settings?.bookableWindow) ? undefined : settings?.bookableWindow?.from}
                    max={isOvernightBookableWindow(settings?.bookableWindow) ? undefined : settings?.bookableWindow?.to}
                    value={modifyStartTime}
                    onChange={(event) => setModifyStartTime(event.target.value)}
                    disabled={!editTime || isModifyLocked || isSavingModification || isSubmittingAction}
                    className={`w-full p-3 border ${theme.radiusClass} focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
                    style={{
                      ...surfaceStyle,
                      color: 'var(--manage-text-primary)',
                      borderColor: panelBorder,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t.endTime}</label>
                  <input
                    type="time"
                    step={900}
                    min={isOvernightBookableWindow(settings?.bookableWindow) ? undefined : modifyStartTime || settings?.bookableWindow?.from}
                    max={isOvernightBookableWindow(settings?.bookableWindow) ? undefined : settings?.bookableWindow?.to}
                    value={modifyEndTime}
                    onChange={(event) => setModifyEndTime(event.target.value)}
                    disabled={!editTime || isModifyLocked || isSavingModification || isSubmittingAction}
                    className={`w-full p-3 border ${theme.radiusClass} focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
                    style={{
                      ...surfaceStyle,
                      color: 'var(--manage-text-primary)',
                      borderColor: panelBorder,
                    }}
                  />
                </div>
              </div>
              {modifyError && (
                <p className="text-sm mt-3" style={{ color: 'var(--manage-danger)' }}>
                  {modifyError}
                </p>
              )}
              {modifySuccess && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm" style={{ color: 'var(--manage-primary)' }}>
                    {modifySuccess}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--manage-text-secondary)' }}>
                    {isModificationRequestMode ? t.modifyRequestHint : t.modifyNextSteps}
                  </p>
                </div>
              )}
              <button
                onClick={handleModifyReservation}
                disabled={isModifyLocked || isSavingModification || isSubmittingAction}
                className={`${baseButtonClasses.primary} w-full mt-4`}
                style={{
                  ...isModifyLocked ? surfaceStyle : {},
                  backgroundColor: isModifyLocked ? surfaceStyle.backgroundColor : opaqueColor(theme.colors.primary),
                  color: isModifyLocked ? 'var(--manage-text-secondary)' : readableColor(opaqueColor(theme.colors.primary), '#ffffff'),
                  opacity: isModifyLocked ? 0.6 : 1,
                }}
              >
                {isSavingModification ? t.modifySaving : isModificationRequestMode ? t.modifyRequestSave : t.modifySave}
              </button>
            </div>

            {booking.status !== 'cancelled' ? (
              <div
                className={`p-4 border ${theme.radiusClass}`}
                style={{
                  ...panelStyle,
                  borderColor: panelBorder,
                  color: 'var(--manage-text-primary)',
                }}
              >
                <h3 className="text-lg font-semibold">{t.cancelReservation}</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--manage-text-secondary)' }}>
                  {t.cancelReservationHint}
                </p>
                {cancelError && (
                  <p className="text-sm mt-3" style={{ color: 'var(--manage-danger)' }}>
                    {cancelError}
                  </p>
                )}
                <button
                  onClick={() => setIsCancelModalOpen(true)}
                  className={`${baseButtonClasses.primary} w-full mt-4`}
                  style={{ backgroundColor: opaqueColor(theme.colors.danger, '#dc2626'), color: readableColor(opaqueColor(theme.colors.danger, '#dc2626'), '#ffffff') }}
                  disabled={isCancelling || isSubmittingAction}
                  aria-busy={isCancelling || isSubmittingAction}
                >
                  {isCancelling || isSubmittingAction ? t.cancelling : t.cancelReservation}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-lg font-semibold" style={{ color: 'var(--manage-danger)' }}>
                  {t.reservationCancelledSuccess}
                </p>
                <p className="text-sm mt-2" style={{ color: 'var(--manage-text-secondary)' }}>
                  {t.cancelNextSteps}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const footerSection = undefined;

  return (
    <>
      <PublicReservationLayout
        {...baseLayoutProps}
        header={headerSection}
        body={bodySection}
        footer={footerSection}
      />

      {isCancelModalOpen && (
        <div className="modal-006-backdrop z-50 p-4">
          <div
            className={`rounded-2xl shadow-xl w-full max-w-sm p-6 text-center ${theme.radiusClass}`}
            style={{ ...surfaceStyle, color: 'var(--manage-text-primary)' }}
          >
            <h2 className="text-xl font-bold" style={{ color: 'var(--manage-text-primary)' }}>
              {t.areYouSureCancel}
            </h2>
            <p className="my-4" style={{ color: 'var(--manage-text-secondary)' }}>
              {t.cancelConfirmationBody}
            </p>
            <div className="mb-4 text-left">
              <label className="block text-sm font-medium mb-1">
                {t.cancelReasonLabel}
              </label>
              <textarea
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                className={`w-full p-3 border ${theme.radiusClass} focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]`}
                style={{
                  ...panelStyle,
                  color: 'var(--manage-text-primary)',
                  borderColor: panelBorder,
                }}
                rows={3}
                placeholder={t.cancelReasonPlaceholder}
                disabled={isCancelling || isSubmittingAction}
              />
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setIsCancelModalOpen(false)}
                className={`${baseButtonClasses.secondary}`}
                style={{
                  ...surfaceStyle,
                  color: 'var(--manage-text-primary)',
                  borderColor: panelBorder,
                }}
                disabled={isCancelling || isSubmittingAction}
              >
                {t.noKeep}
              </button>
              <button
                onClick={handleCancelReservation}
                className={`${baseButtonClasses.primary}`}
                style={{ backgroundColor: opaqueColor(theme.colors.danger, '#dc2626'), color: readableColor(opaqueColor(theme.colors.danger, '#dc2626'), '#ffffff') }}
                disabled={isCancelling || isSubmittingAction}
                aria-busy={isCancelling || isSubmittingAction}
              >
                {isCancelling || isSubmittingAction ? t.cancelling : t.yesCancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageReservationPage;
